<template>
  <div>
    <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-hidden="true" :class="{'show d-block': isModalOpen}" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('upload_transactions')}}</h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />
              {{$t('upload_transactions_info')}}
            </div>

            <!-- CSV Upload -->
            <div class="mb-4">
              <label class="form-label">{{$t('upload_csv_file')}}</label>
              <input type="file" class="form-control" @change="handleFileUpload" accept=".csv">
              <small class="text-muted">{{$t('csv_format_hint')}}</small>
            </div>

            <!-- Manual Entry -->
            <div class="mb-3">
              <h6>{{$t('or_add_manually')}}</h6>
              <button @click="addTransaction" class="btn btn-sm btn-outline-primary">
                <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-2" />{{$t('add_transaction')}}
              </button>
            </div>

            <!-- Transactions List -->
            <div v-if="transactions.length > 0" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>{{$t('date')}}</th>
                    <th>{{$t('type')}}</th>
                    <th>{{$t('amount')}}</th>
                    <th>{{$t('reference')}}</th>
                    <th>{{$t('description')}}</th>
                    <th>{{$t('category')}}</th>
                    <th>{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(transaction, index) in transactions" :key="index">
                    <td>
                      <date-picker :clearable="false" :editable="false" v-model="transaction.transaction_date" type="date" format="YYYY-MM-DD" value-type="YYYY-MM-DD" style="width: 130px"></date-picker>
                    </td>
                    <td>
                      <select class="form-select form-select-sm" v-model="transaction.type" style="width: 110px">
                        <option value="incoming">{{$t('incoming')}}</option>
                        <option value="outgoing">{{$t('outgoing')}}</option>
                      </select>
                    </td>
                    <td>
                      <input type="number" step="0.01" class="form-control form-control-sm" v-model="transaction.amount" style="width: 100px">
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="transaction.reference" style="width: 120px">
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="transaction.description" style="width: 150px">
                    </td>
                    <td>
                      <input type="text" class="form-control form-control-sm" v-model="transaction.category" style="width: 120px">
                    </td>
                    <td>
                      <button @click="removeTransaction(index)" class="btn btn-sm btn-danger">
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="transactions.length > 0" class="alert alert-success mt-3">
              <strong>{{transactions.length}}</strong> {{$t('transactions_ready_to_upload')}}
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">{{$t('cancel')}}</button>
              <button type="button" class="btn btn-primary" @click="submit" :disabled="loading || transactions.length === 0">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                {{$t('upload')}} ({{transactions.length}})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'
import helper from '../../helper'

export default {
  name: 'UploadBankTransactionsModal',
  components: {
    DatePicker
  },
  props: {
    isModalOpen: {
      type: Boolean,
      default: false
    },
    modalId: {
      type: String,
      default: 'upload-transactions-modal'
    },
    bankAccountId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      transactions: [],
      loading: false
    }
  },
  methods: {
    addTransaction() {
      this.transactions.push({
        transaction_date: moment().format('YYYY-MM-DD'),
        type: 'incoming',
        amount: 0,
        reference: '',
        description: '',
        category: '',
        notes: ''
      })
    },
    removeTransaction(index) {
      this.transactions.splice(index, 1)
    },
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const csv = e.target.result
          const lines = csv.split('\n')
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
          
          // Parse CSV
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue
            
            const values = lines[i].split(',')
            const transaction = {}
            
            headers.forEach((header, index) => {
              const value = values[index]?.trim() || ''
              
              // Map common CSV headers
              if (header.includes('date')) {
                transaction.transaction_date = moment(value).format('YYYY-MM-DD')
              } else if (header.includes('type')) {
                transaction.type = value.toLowerCase().includes('in') ? 'incoming' : 'outgoing'
              } else if (header.includes('amount')) {
                const cleanAmount = value.replace(/[^0-9.-]/g, '')
                transaction.amount = parseFloat(cleanAmount)
              } else if (header.includes('ref')) {
                transaction.reference = value
              } else if (header.includes('desc')) {
                transaction.description = value
              } else if (header.includes('cat')) {
                transaction.category = value
              }
            })
            
            // Validate and add - ensure amount is a valid number
            if (transaction.transaction_date && !isNaN(transaction.amount) && transaction.amount > 0) {
              this.transactions.push({
                transaction_date: transaction.transaction_date,
                type: transaction.type || 'incoming',
                amount: Number(transaction.amount),
                reference: transaction.reference || '',
                description: transaction.description || '',
                category: transaction.category || '',
                notes: ''
              })
            }
          }
          
          this.$toast.success(`${this.transactions.length} transactions loaded from CSV`)
        } catch (error) {
          alert(error)
          this.$toast.error('Error parsing CSV file')
        }
      }
      reader.readAsText(file)
    },
    async submit() {
      if (this.transactions.length === 0) {
        this.$toast.warning('Please add at least one transaction')
        return
      }

      try {
        this.loading = true
        
        await helper.customPost(`${process.env.VUE_APP_API}/bank-transactions/upload`, {
          bank_account_id: this.bankAccountId,
          transactions: this.transactions
        }, {showToast: true})
        
        this.loading = false
        this.$emit('refresh')
        this.closeModal()
      } catch (error) {
        this.loading = false
        alert(error)
      }
    },
    closeModal() {
      this.transactions = []
      this.$emit('modalClosed')
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block;
}
</style>
